import { Button } from "@mixitone/components";
import { type Club, type ClubPrintPaletteId, RecurringSession } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import ClubDirectoryPdfPreviewModal, {
  type ClubDirectoryPdfPreview,
} from "components/Forms/ClubDirectoryPdfPreviewModal";
import {
  type ClubDirectoryPdfSession,
  clubDirectoryPdfClubFromRecord,
  clubDirectoryPdfSessionsFromRecords,
  createClubDirectoryCardPdfBlob,
  createClubDirectoryPdfBlob,
} from "lib/clubDirectoryPdf";
import { resolvePrintPaletteId } from "lib/clubDirectoryPrintPalettes";
import { clubDirectoryListingUrl } from "lib/directoryUrls";
import { useRef, useState } from "react";
import { messagesService } from "services";

type Props = {
  club: Club;
  onPrintPaletteChange: (id: ClubPrintPaletteId) => Promise<void>;
};

type PreviewKind = "flyer" | "card";

const ClubDirectoryListingActions: React.FC<Props> = ({ club, onPrintPaletteChange }) => {
  const [generating, setGenerating] = useState<PreviewKind | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [preview, setPreview] = useState<ClubDirectoryPdfPreview | null>(null);
  const [paletteId, setPaletteId] = useState<ClubPrintPaletteId>(() =>
    resolvePrintPaletteId(club.getPreference("printPalette")),
  );
  const sessionsRef = useRef<ClubDirectoryPdfSession[]>([]);
  const listingUrl = clubDirectoryListingUrl(club);

  if (!listingUrl) return null;

  const loadListedSessions = async (clubId: string) => {
    const sessions = await RecurringSession.query()
      .eq("club_id", clubId)
      .eq("listed_in_directory", true)
      .all();
    return clubDirectoryPdfSessionsFromRecords(sessions.filter((session) => session.is_deleted !== true));
  };

  const buildPreview = async (kind: PreviewKind, nextPaletteId: ClubPrintPaletteId) => {
    const clubRecord = clubDirectoryPdfClubFromRecord(club);
    const result =
      kind === "flyer"
        ? await createClubDirectoryPdfBlob(clubRecord, sessionsRef.current, listingUrl, nextPaletteId)
        : await createClubDirectoryCardPdfBlob(clubRecord, sessionsRef.current, listingUrl, nextPaletteId);
    return { kind, blob: result.blob, filename: result.filename };
  };

  const handlePreview = async (kind: PreviewKind) => {
    if (!club.id || generating) return;

    setGenerating(kind);
    try {
      sessionsRef.current = await loadListedSessions(club.id);
      setPreview(await buildPreview(kind, paletteId));
    } catch (error) {
      console.error(`Failed to generate club ${kind} PDF`, error);
      messagesService.addMessage(
        kind === "flyer" ? "Failed to generate directory PDF" : "Failed to generate club card PDF",
        "error",
      );
    } finally {
      setGenerating(null);
    }
  };

  const handlePaletteChange = async (nextPaletteId: ClubPrintPaletteId) => {
    if (!preview || nextPaletteId === paletteId || regenerating) return;

    setPaletteId(nextPaletteId);
    setRegenerating(true);
    try {
      await onPrintPaletteChange(nextPaletteId);
      setPreview(await buildPreview(preview.kind, nextPaletteId));
    } catch (error) {
      console.error("Failed to update print colours", error);
      messagesService.addMessage("Failed to update print colours", "error");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="mt-2 space-y-3">
      <p className="text-sm text-gray-600">
        <a
          href={listingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          View your club listing
        </a>
      </p>

      <div className="rounded-lg border border-brand-200 bg-gradient-to-br from-brand-50 via-white to-brand-100/50 p-4 shadow-sm ring-1 ring-brand-100/80">
        <p className="text-sm font-semibold text-slate-900">Printable club materials</p>
        <p className="mt-1 text-sm leading-relaxed text-gray-600">
          Preview a flyer for noticeboards, or a business card to hand out. Both include a QR code to your
          directory listing. Download from the preview when you are ready.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            compact
            spinner={generating === "flyer"}
            disabled={!!generating}
            onClick={() => handlePreview("flyer")}
          >
            {generating === "flyer" ? "Generating PDF..." : "View flyer"}
          </Button>
          <Button
            compact
            spinner={generating === "card"}
            disabled={!!generating}
            onClick={() => handlePreview("card")}
          >
            {generating === "card" ? "Generating PDF..." : "View card"}
          </Button>
        </div>
      </div>

      {preview && (
        <ClubDirectoryPdfPreviewModal
          preview={preview}
          paletteId={paletteId}
          regenerating={regenerating}
          onPaletteChange={handlePaletteChange}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default ApplicationView(ClubDirectoryListingActions);
